import React, { useState, useCallback } from 'react';
import { clinicService } from '../../../services/api';

const Step4_Confirm = ({ clinicId, qaItems, onBack, onComplete, error, setError }) => {
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const handleConfirm = useCallback(async () => {
    const items = qaItems.map((item) => ({
      language: item.language || 'zh_HK',
      question: item.question?.trim(),
      answer: item.answer?.trim(),
      tags: item.tags?.length ? item.tags : ['Appointment Flow'],
    }));

    const invalid = items.filter((i) => !i.question || !i.answer);
    if (invalid.length > 0) {
      setError(`${invalid.length} item(s) have empty question or answer`);
      return;
    }

    setApplying(true);
    setError('');
    setResult(null);

    try {
      const data = await clinicService.importQAItems(clinicId, items, 'always');
      setResult({
        success: true,
        importedCount: data?.imported_count ?? data?.length ?? items.length,
        failedCount: data?.failed_count ?? 0,
        failedItems: data?.failed_items ?? [],
      });
      onComplete?.({ success: true, ...data });
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.detail || err.message });
      setError(err.response?.data?.detail || err.message || 'Import failed');
      onComplete?.({ success: false });
    } finally {
      setApplying(false);
    }
  }, [clinicId, qaItems, onComplete, setError]);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 4: Confirm</h2>
        <p className="text-gray-600">
          Import {qaItems?.length || 0} QA item{qaItems?.length !== 1 ? 's' : ''}? Attach rule: always.
        </p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          Existing items with matching question will be updated. New items will be created. Tags will
          be created if they do not exist.
        </p>
      </div>

      <div className="border rounded-lg max-h-48 overflow-y-auto">
        {qaItems?.map((item, i) => (
          <div key={i} className="p-3 border-b last:border-b-0">
            <span className="font-medium text-gray-900">{item.question}</span>
            <span className="text-gray-500 text-sm ml-2">
              ({item.tags?.join(', ') || 'Appointment Flow'})
            </span>
          </div>
        ))}
      </div>

      {result && (
        <div className="space-y-2">
          {result.success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="font-medium text-green-800">
                Imported {result.importedCount} item{result.importedCount !== 1 ? 's' : ''}.
                {result.failedCount > 0 && ` ${result.failedCount} failed.`}
              </p>
              {result.failedItems?.length > 0 && (
                <ul className="list-disc list-inside text-sm text-green-700 mt-2">
                  {result.failedItems.map((f, i) => (
                    <li key={i}>
                      {f.item?.question}: {f.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {!result.success && result.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="font-medium text-red-800">{result.error}</p>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          disabled={applying}
          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium disabled:opacity-50"
        >
          Back
        </button>
        <button
          onClick={handleConfirm}
          disabled={applying}
          className="px-8 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {applying ? 'Importing...' : 'Confirm'}
        </button>
      </div>
    </div>
  );
};

export default Step4_Confirm;
